import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/common.css';

function ApprovalManagement() {
  const [user, setUser] = useState(null);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [evidenceThumbnails, setEvidenceThumbnails] = useState({});
  const [approvalModal, setApprovalModal] = useState({ show: false, assignment: null });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      fetchPendingApprovals();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/parent/login');
    }
  }, [navigate]);

  // 화면 복귀 시 자동 새로고침 + 30초 폴링
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchPendingApprovals();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(fetchPendingApprovals, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await api.get('/assignments/pending-approvals', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPendingAssignments(response.data.assignments);
    } catch (error) {
      console.error('승인 대기 목록 조회 오류:', error);
      alert('승인 대기 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pendingAssignments.length > 0) {
      loadEvidenceThumbnails();
    }
  }, [pendingAssignments]);

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 썸네일 URL 정리
      Object.values(evidenceThumbnails).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [evidenceThumbnails]);

  const handleApprove = (assignment, approve) => {
    setApprovalModal({ show: true, assignment, approve });
  };

  const submitApproval = async (reason = '') => {
    if (!approvalModal.assignment) return;

    try {
      setProcessingId(approvalModal.assignment.id);
      const token = localStorage.getItem('token');

      await api.post(`/assignments/${approvalModal.assignment.id}/approve`, {
        approve: approvalModal.approve,
        reason: reason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(approvalModal.approve ? '미션이 승인되었습니다!' : '미션이 반려되었습니다.');

      // 목록 새로고침
      fetchPendingApprovals();
      setApprovalModal({ show: false, assignment: null });

    } catch (error) {
      console.error('승인 처리 오류:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const loadEvidenceThumbnails = async () => {
    const thumbnails = {};
    for (const assignment of pendingAssignments) {
      if (assignment.has_evidence) {
        try {
          const token = localStorage.getItem('token');
          const response = await api.get(`/assignments/${assignment.id}/evidence`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });

          const imageUrl = URL.createObjectURL(response.data);
          thumbnails[assignment.id] = imageUrl;
        } catch (error) {
          console.error(`썸네일 로드 실패 (${assignment.id}):`, error);
        }
      }
    }
    setEvidenceThumbnails(thumbnails);
  };

  const viewEvidence = async (assignmentId) => {
    if (evidenceThumbnails[assignmentId]) {
      setSelectedEvidence(evidenceThumbnails[assignmentId]);
    } else {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/assignments/${assignmentId}/evidence`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });

        const imageUrl = URL.createObjectURL(response.data);
        setSelectedEvidence(imageUrl);
      } catch (error) {
        console.error('증빙 사진 조회 오류:', error);
        alert('증빙 사진을 불러올 수 없습니다.');
      }
    }
  };

  // 가중치 변환 함수
  const getWeightDisplayText = (numWeight) => {
    if (numWeight >= 1.5) return '상 (1.5배)';
    if (numWeight <= 0.7) return '하 (0.7배)';
    return '중 (1.0배)';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user || loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="approval-container">
      {/* 헤더 */}
      <div className="approval-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
            ← 뒤로가기
          </button>
          <h1>승인 대기 목록</h1>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="approval-content">
        {pendingAssignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>승인 대기 중인 미션이 없습니다</h3>
            <p>자녀들이 미션을 완료하면 여기에 표시됩니다</p>
          </div>
        ) : (
          <div className="assignments-list">
            {pendingAssignments.map((assignment) => (
              <div key={assignment.id} className="assignment-card">
                <div className="assignment-header">
                  <div className="mission-info">
                    <h3>{assignment.title}</h3>
                    <div className="mission-meta">
                      <p className="child-name">👶 {assignment.child_name}</p>
                      {assignment.weight && (
                        <span className="mission-weight">
                          ⚖️ {getWeightDisplayText(parseFloat(assignment.weight))}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="check-time">
                    <span>{formatDate(assignment.checked_at)}</span>
                  </div>
                </div>

                {assignment.description && (
                  <p className="mission-description">{assignment.description}</p>
                )}

                {assignment.note && (
                  <div className="assignment-note">
                    <strong>자녀 메모:</strong> {assignment.note}
                  </div>
                )}

                <div className="assignment-actions">
                  {assignment.has_evidence && (
                    <div className="evidence-section">
                      {evidenceThumbnails[assignment.id] ? (
                        <div className="evidence-thumbnail-container">
                          <img
                            src={evidenceThumbnails[assignment.id]}
                            alt="증빙 사진 썸네일"
                            className="evidence-thumbnail"
                            onClick={() => viewEvidence(assignment.id)}
                          />
                          <button
                            className="thumbnail-overlay"
                            onClick={() => viewEvidence(assignment.id)}
                          >
                            🔍 확대보기
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-outline evidence-btn"
                          onClick={() => viewEvidence(assignment.id)}
                        >
                          📷 증빙 사진
                        </button>
                      )}
                    </div>
                  )}

                  <div className="approval-buttons">
                    <button
                      className="btn btn-danger"
                      onClick={() => handleApprove(assignment, false)}
                      disabled={processingId === assignment.id}
                    >
                      반려
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleApprove(assignment, true)}
                      disabled={processingId === assignment.id}
                    >
                      {processingId === assignment.id ? '처리중...' : '승인'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 승인/반려 모달 */}
      {approvalModal.show && (
        <div className="modal-backdrop">
          <div className="modal approval-modal">
            <div className="modal-header">
              <h3>{approvalModal.approve ? '미션 승인' : '미션 반려'}</h3>
            </div>
            <div className="modal-body">
              <div className="mission-summary">
                <h4>{approvalModal.assignment.title}</h4>
                <p>👶 {approvalModal.assignment.child_name}</p>
              </div>

              <div className="form-group">
                <label>
                  {approvalModal.approve ? '승인 메모 (선택사항)' : '반려 사유'}
                </label>
                <textarea
                  id="reason"
                  rows="3"
                  placeholder={approvalModal.approve ?
                    '잘했어요! 격려 메시지를 남겨주세요.' :
                    '반려 이유를 구체적으로 설명해주세요.'
                  }
                  maxLength="500"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => setApprovalModal({ show: false, assignment: null })}
              >
                취소
              </button>
              <button
                className={`btn ${approvalModal.approve ? 'btn-primary' : 'btn-danger'}`}
                onClick={() => {
                  const reason = document.getElementById('reason').value;
                  submitApproval(reason);
                }}
                disabled={processingId}
              >
                {approvalModal.approve ? '승인' : '반려'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 증빙 사진 모달 */}
      {selectedEvidence && (
        <div className="modal-backdrop" onClick={() => {
          setSelectedEvidence(null);
          URL.revokeObjectURL(selectedEvidence);
        }}>
          <div className="modal evidence-modal">
            <div className="modal-header">
              <h3>증빙 사진</h3>
              <button
                className="close-button"
                onClick={() => {
                  setSelectedEvidence(null);
                  URL.revokeObjectURL(selectedEvidence);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <img src={selectedEvidence} alt="증빙 사진" className="evidence-image" />
            </div>
          </div>
        </div>
      )}

      <style>{`
        .approval-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }

        .approval-header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 15px 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          align-items: center;
          gap: 15px;
        }


        .approval-header h1 {
          margin: 0;
          color: #333;
          font-size: 1.5rem;
        }

        .approval-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #333;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #666;
        }

        .assignments-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .assignment-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .assignment-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .assignment-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
        }

        .mission-info h3 {
          color: #333;
          margin: 0 0 5px 0;
          font-size: 1.3rem;
        }

        .mission-meta {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .child-name {
          color: #8B4513;
          font-weight: 500;
          margin: 0;
        }

        .mission-weight {
          color: #666;
          font-size: 0.9rem;
          font-weight: 500;
          background: #f0f0f0;
          padding: 2px 8px;
          border-radius: 12px;
          display: inline-block;
          width: fit-content;
        }

        .check-time {
          color: #666;
          font-size: 0.9rem;
        }

        .mission-description {
          color: #666;
          margin-bottom: 15px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .assignment-note {
          background: #e3f2fd;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          color: #1976d2;
        }

        .assignment-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
        }

        .evidence-btn {
          white-space: nowrap;
        }

        .evidence-section {
          margin-bottom: 15px;
        }

        .evidence-thumbnail-container {
          position: relative;
          display: inline-block;
          cursor: pointer;
        }

        .evidence-thumbnail {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          transition: all 0.2s;
        }

        .evidence-thumbnail:hover {
          border-color: #8B4513;
          transform: scale(1.05);
        }

        .thumbnail-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 10px;
          opacity: 0;
          transition: opacity 0.2s;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          line-height: 1.2;
        }

        .evidence-thumbnail-container:hover .thumbnail-overlay {
          opacity: 1;
        }

        .approval-buttons {
          display: flex;
          gap: 10px;
        }

        .approval-buttons .btn {
          min-width: 80px;
        }

        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .evidence-modal {
          max-width: 90%;
          max-height: 90%;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #333;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 5px;
          line-height: 1;
        }

        .close-button:hover {
          color: #333;
        }

        .modal-body {
          padding: 20px;
        }

        .mission-summary {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .mission-summary h4 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .mission-summary p {
          margin: 0;
          color: #8B4513;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #333;
        }

        .form-group textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: #8B4513;
          box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
        }

        .modal-actions {
          padding: 20px;
          border-top: 1px solid #eee;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .evidence-image {
          width: 100%;
          height: auto;
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .assignment-header {
            flex-direction: column;
            gap: 10px;
          }

          .assignment-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .approval-buttons {
            justify-content: space-between;
          }

          .modal {
            margin: 10px;
            max-height: calc(100vh - 20px);
          }
        }
      `}</style>
    </div>
  );
}

export default ApprovalManagement;